import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Loader2, Copy } from "lucide-react";

export const ExternalSiteIntegrationSection = ({ authAxios }) => {
  const [externalSiteData, setExternalSiteData] = useState(null);
  const [externalSiteLoading, setExternalSiteLoading] = useState(false);
  const [externalSiteKey, setExternalSiteKey] = useState("");

  const generateExternalSiteKey = async () => {
    setExternalSiteLoading(true);
    try {
      const res = await authAxios.post("/restaurant/integrations/external-site/generate-key");
      setExternalSiteKey(res.data.api_key);
      setExternalSiteData(res.data);
      toast.success("API key generada. Copie y guarde en lugar seguro.");
    } catch { toast.error("Error generando API key"); }
    setExternalSiteLoading(false);
  };

  const rotateExternalSiteKey = async () => {
    setExternalSiteLoading(true);
    try {
      const res = await authAxios.post("/restaurant/integrations/external-site/rotate-key");
      setExternalSiteKey(res.data.api_key);
      setExternalSiteData(res.data);
      toast.success("API key rotada. Key anterior ya no es valida.");
    } catch { toast.error("Error rotando API key"); }
    setExternalSiteLoading(false);
  };

  const disableExternalSite = async () => {
    if (!window.confirm("Desabilitar integracion externa? Las keys existentes ya no seran validas.")) return;
    setExternalSiteLoading(true);
    try {
      await authAxios.delete("/restaurant/integrations/external-site");
      setExternalSiteData(null);
      setExternalSiteKey("");
      toast.success("Integracion deshabilitada");
    } catch { toast.error("Error deshabilitando integracion"); }
    setExternalSiteLoading(false);
  };

  return (
    <Card className="bg-card border-border/50 border-l-4 border-l-blue-500">
      <CardHeader>
        <CardTitle className="uppercase">Sitio Externo (Plugin Genérico)</CardTitle>
        <CardDescription>Conectar un sitio web externo para reservas via API.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!externalSiteData ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Habilita tu sitio externo para crear reservas directamente en el Kaiso System via API segura con autenticacion por clave.
            </p>
            <Button onClick={generateExternalSiteKey} disabled={externalSiteLoading} size="sm">
              {externalSiteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Generar API Key
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                Activo
              </Badge>
              <span className="text-sm text-muted-foreground">Creado: {new Date(externalSiteData.created_at).toLocaleDateString()}</span>
            </div>
            {externalSiteData.last_used_at && (
              <p className="text-xs text-muted-foreground">
                Ultimo uso: {new Date(externalSiteData.last_used_at).toLocaleString()}
              </p>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Identificador de Clave (para referencia)</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-muted rounded px-2 py-1 font-mono truncate">
                  {externalSiteData.key_prefix}...
                </code>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Endpoint de Disponibilidad</Label>
              <code className="text-xs bg-muted rounded px-2 py-1 block font-mono break-all">
                GET /api/plugins/external-site/reservations/availability?date=YYYY-MM-DD&guests=N
              </code>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Endpoint de Creacion</Label>
              <code className="text-xs bg-muted rounded px-2 py-1 block font-mono break-all">
                POST /api/plugins/external-site/reservations
              </code>
              <p className="text-xs text-muted-foreground">
                Header: <code className="bg-muted px-1">X-API-Key: [tu-api-key]</code>
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={rotateExternalSiteKey} disabled={externalSiteLoading} size="sm" variant="outline">
                {externalSiteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Rotar Key
              </Button>
              <Button onClick={disableExternalSite} disabled={externalSiteLoading} size="sm" variant="destructive">
                Deshabilitar
              </Button>
            </div>
          </div>
        )}
        {externalSiteKey && (
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-2">
            <p className="text-sm font-medium text-emerald-700">API Key Generada (mostrada solo una vez):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white rounded px-2 py-2 font-mono break-all text-emerald-900 select-all">
                {externalSiteKey}
              </code>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => { navigator.clipboard.writeText(externalSiteKey); toast.success("Key copiada!"); }}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-emerald-600">
              ⚠️ Guarda esta clave en lugar seguro. No sera mostrada nuevamente. Si la pierdes, genera una nueva key.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
